import React, { useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { PencilSquareIcon, XMarkIcon, MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import InfoTip from './InfoTip';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { useClients, useTarifas, useContracts } from '../contexts/DataContext';
import { diagnoseComisionPct, formatNumeroFianza } from '../utils/calculations';
import { useUser } from './UserContext';
import { useComisionLedger } from '../hooks/useComisionLedger';
import { ComisionEditorDialog } from './ComisionEditorDialog';
import SearchableListbox from './SearchableListbox';
import { Heading } from '../components/heading';
import { Text } from '../components/text';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/table';
import { Badge } from '../components/badge';
import { FianzaView } from '../components/FianzaView';

// El ledger liquida al centavo: la comisión de una fianza rara vez cae en
// pesos redondos y el agente concilia contra el estado de cuenta de la
// afianzadora, así que aquí los centavos se muestran siempre.
const fmtMXN = (n) =>
  new Intl.NumberFormat('es-MX', {
    style: 'currency',
    currency: 'MXN',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Number(n) || 0);

const fmtFecha = (ms) =>
  ms
    ? new Date(Number(ms)).toLocaleDateString('es-MX', { day: '2-digit', month: 'short', year: 'numeric' })
    : '—';

// Payment state comes DENORMALIZED on the movement (facturaPagada, kept current
// by the /factura-pagada endpoint) — no contract loading needed. A missing flag
// (movement predates the field) reads as unpaid, the conservative bucket: it
// shows as "aún no exigible", never as money the afianzadora owes today.
const facturaPagada = (m) => m.facturaPagada === true;

// Afianzadora names show exactly as the ledger has them — the old short-name
// mapping relabeled some carriers wrong.
const afianzadoraLabel = (name) => name || 'Sin afianzadora';

const TIPO_LABELS = {
  emision: 'Emisión',
  adicional: 'Endoso',
  sustituye: 'Sustitución',
  cargo: 'Cargo',
};

// Case- and accent-insensitive matching, so "emision" finds "Emisión".
const normalizar = (s) =>
  String(s || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');

// The tiles ARE the estado filter: one vocabulary for the whole page
// (matching the saldos table), one click to slice the movements.
function StatTile({ label, value, sub, tone, active, onClick }) {
  const tones = {
    green: 'text-emerald-700',
    amber: 'text-amber-700',
    red: 'text-red-700',
    default: 'text-azul-surelia',
  };
  return (
    <button
      type="button"
      onClick={onClick}
      className={`text-left bg-white rounded-lg border shadow-sm p-4 transition-all ${
        active
          ? 'border-azul-surelia ring-2 ring-azul-surelia/30'
          : 'border-gray-200 hover:border-azul-surelia/40'
      }`}
    >
      <div className="text-[10px] uppercase tracking-wide text-gray-500">{label}</div>
      <div className={`text-xl font-bold ${tones[tone] || tones.default}`}>{value}</div>
      <div className="text-[11px] text-gray-500 mt-0.5">{sub || ' '}</div>
    </button>
  );
}

function EstadoBadge({ mov }) {
  if (mov.estadoCobro === 'cobrada') {
    return (
      <div>
        <Badge color="green">Cobrada</Badge>
        {mov.cobradaFecha && (
          <div className="text-[10px] text-gray-500 mt-0.5">{fmtFecha(mov.cobradaFecha)}</div>
        )}
      </div>
    );
  }
  if (mov.estadoCobro === 'pendiente') {
    return <Badge color="amber">Por cobrar</Badge>;
  }
  return <Badge color="zinc">{mov.estadoCobro || '—'}</Badge>;
}

/**
 * ClientComisiones — the comisión ledger, readable.
 *
 * One row per movement (emisión / endoso / cargo), straight from
 * orgs/{org}/comisiones. With a `clientId` it is that cliente's slice; without
 * one it is the org-wide view (a Cliente column appears). Shows what was
 * earned, what has been collected, what is still owed, and lets the user drill
 * into the fianza. Fianzas without ledger movements (org sin tarifa,
 * pre-backfill) simply do not appear — this view reads the ledger only, it
 * never estimates.
 */
export function ClientComisiones({ clientId = null, convenioId = null }) {
  const { movements, loading } = useComisionLedger();
  const { clientsById } = useClients();
  const { contractsList } = useContracts();
  const { tarifas } = useTarifas();
  const { isOwner } = useUser();
  const [filter, setFilter] = useState('todas');
  const [busqueda, setBusqueda] = useState('');
  const [clienteFilter, setClienteFilter] = useState('todos');
  const [afianzadoraFilter, setAfianzadoraFilter] = useState('todas');
  const [selectedFianza, setSelectedFianza] = useState(null);
  const [editMov, setEditMov] = useState(null); // movement whose % is being corrected
  const [saldoDetalle, setSaldoDetalle] = useState(null); // { afianzadora, bucket } for the slide-over

  // El movimiento del ledger guarda numeroFianza pero no la inclusión, así que
  // ésta se resuelve contra la fianza viva. Vale para los movimientos que ya
  // estaban escritos, sin backfill.
  const inclusionPorFianza = useMemo(() => {
    const map = new Map();
    contractsList.forEach((c) =>
      (c.fianzas || []).forEach((f) => {
        const inclusion = f.numeroInclusion || f.xmlFianza?.numeroInclusion;
        if (f.id && inclusion) map.set(f.id, inclusion);
      })
    );
    return map;
  }, [contractsList]);

  const numeroFianzaLabel = (m) =>
    formatNumeroFianza(
      { numeroFianza: m.numeroFianza, numeroInclusion: inclusionPorFianza.get(m.fianzaId) },
      'Sin número'
    );

  const clientMovs = useMemo(
    () =>
      movements
        .filter((m) => (clientId ? m.clienteId === clientId : true))
        .filter((m) => (convenioId ? m.convenioId === convenioId : true))
        .sort((a, b) => (Number(b.fecha) || 0) - (Number(a.fecha) || 0)),
    [movements, clientId, convenioId]
  );

  // Filter options, derived from what actually exists in the ledger.
  const clienteOptions = useMemo(() => {
    if (clientId) return [];
    const map = new Map();
    clientMovs.forEach((m) => {
      if (!m.clienteId) return;
      map.set(m.clienteId, clientsById[m.clienteId]?.nombreEmpresa || m.clienteNombre || m.clienteId);
    });
    return Array.from(map.entries())
      .map(([id, nombre]) => ({ id, nombre }))
      .sort((a, b) => a.nombre.localeCompare(b.nombre));
  }, [clientMovs, clientsById, clientId]);

  const afianzadoraOptions = useMemo(() => {
    const set = new Set();
    clientMovs.forEach((m) => { if (m.afianzadora) set.add(m.afianzadora); });
    return Array.from(set).sort();
  }, [clientMovs]);

  // Cliente/afianzadora filters shape the whole view (tiles included); the
  // estado pills only slice the table.
  const base = useMemo(
    () =>
      clientMovs.filter((m) => {
        if (clienteFilter !== 'todos' && m.clienteId !== clienteFilter) return false;
        if (afianzadoraFilter !== 'todas' && m.afianzadora !== afianzadoraFilter) return false;
        return true;
      }),
    [clientMovs, clienteFilter, afianzadoraFilter]
  );


  // The BROKER's saldo with each afianzadora, straight from the ledger. A
  // pendiente comisión splits on whether its source factura is pagada:
  //   cobrable  — factura pagada, comisión not yet marked cobrada → the
  //               afianzadora owes the agent this money NOW.
  //   porExigir — factura sin pagar → earned, but not collectable until the
  //               cliente pays the prima.
  //   adeudo    — reversed comisión the agent must give back (nets against
  //               future liquidaciones).
  const saldos = useMemo(() => {
    const byAfianzadora = new Map();
    const totals = { cobrada: 0, cobrable: 0, porExigir: 0, adeudo: 0, neto: 0 };
    base.forEach((m) => {
      const monto = Number(m.comisionMonto) || 0;
      if (m.estadoCobro === 'cobrada' && monto > 0) {
        totals.cobrada += monto;
        return;
      }
      let bucket = null;
      if (m.estadoCobro === 'pendiente' && monto > 0) {
        bucket = facturaPagada(m) ? 'cobrable' : 'porExigir';
      } else if (m.estadoCobro === 'adeudo') {
        bucket = 'adeudo';
      }
      if (!bucket) return;

      const key = m.afianzadora || 'Sin afianzadora';
      const entry = byAfianzadora.get(key) || {
        afianzadora: key, cobrable: 0, porExigir: 0, adeudo: 0, neto: 0,
        cobrableMovs: [], porExigirMovs: [], adeudoMovs: [],
      };
      entry[bucket] += monto;
      entry[`${bucket}Movs`].push(m);
      byAfianzadora.set(key, entry);
      totals[bucket] += monto;
    });
    byAfianzadora.forEach((e) => { e.neto = e.cobrable + e.porExigir + e.adeudo; });
    totals.neto = totals.cobrable + totals.porExigir + totals.adeudo;
    return {
      ...totals,
      porAfianzadora: Array.from(byAfianzadora.values())
        .sort((a, b) => b.neto - a.neto),
    };
  }, [base]);

  // Fianzas whose booked movements carry no % (pct-pendiente: sin tarifa que
  // los precie, aún sin confirmar). Reversas quedan fuera — su monto deriva
  // del movimiento revertido, no de una tabla.
  const sinPctFianzas = useMemo(() => {
    const ids = new Set();
    base.forEach((m) => {
      if (m.comisionPct == null && !m.manualOverride && !m.reversaDeMovId && m.fianzaId) {
        ids.add(m.fianzaId);
      }
    });
    return ids;
  }, [base]);

  // The tiles filter the table on the same buckets the saldos use; the search
  // box slices the table too (tiles and saldos stay whole, like the pills).
  const visible = useMemo(() => {
    let rows = base;
    if (filter !== 'todas') {
      rows = rows.filter((m) => {
        const monto = Number(m.comisionMonto) || 0;
        switch (filter) {
          case 'cobrada':
            return m.estadoCobro === 'cobrada';
          case 'cobrable':
            return m.estadoCobro === 'pendiente' && monto > 0 && facturaPagada(m);
          case 'porExigir':
            return m.estadoCobro === 'pendiente' && monto > 0 && !facturaPagada(m);
          case 'adeudo':
            return m.estadoCobro === 'adeudo' || (Number(m.comisionMonto) || 0) < 0;
          case 'sinPct':
            return m.comisionPct == null && !m.manualOverride && !m.reversaDeMovId;
          default:
            return true;
        }
      });
    }
    const q = normalizar(busqueda.trim());
    if (q) {
      rows = rows.filter((m) =>
        [
          m.numeroFianza,
          inclusionPorFianza.get(m.fianzaId),
          clientsById[m.clienteId]?.nombreEmpresa || m.clienteNombre,
          m.afianzadora,
          m.beneficiarioNombre,
          m.fiadoNombre,
          m.contratoNumero,
          TIPO_LABELS[m.tipo] || m.tipo,
        ].some((campo) => normalizar(campo).includes(q))
      );
    }
    return rows;
  }, [base, filter, busqueda, clientsById, inclusionPorFianza]);

  const toggleFilter = (key) => setFilter((f) => (f === key ? 'todas' : key));

  // On-demand single-contract read: the page itself never loads contracts —
  // only opening a fianza fetches the one document it needs.
  const toggleSaldoDetalle = (afianzadora, bucket) =>
    setSaldoDetalle((prev) =>
      prev && prev.afianzadora === afianzadora && prev.bucket === bucket
        ? null
        : { afianzadora, bucket }
    );

  const openFianza = async (mov) => {
    if (!mov.contratoId) return;
    try {
      const snap = await getDoc(doc(db, 'contracts', mov.contratoId));
      if (!snap.exists()) return;
      const contract = { id: snap.id, ...snap.data() };
      const fianza = (contract.fianzas || []).find((f) => f.id === mov.fianzaId);
      if (!fianza) return;
      setSelectedFianza({
        ...fianza,
        contratoId: fianza.contratoId || contract.id,
        clienteIdRef: contract.clientId,
        contratoRef: contract,
      });
    } catch (e) {
      console.error('open fianza failed', e);
    }
  };

  if (loading) {
    return <div className="py-8 text-center text-azul-surelia/70">Cargando datos…</div>;
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-1">
        <Heading level={2} className="text-xl font-bold text-azul-surelia">
          Comisiones
        </Heading>
        <Text className="text-sm text-azul-surelia/70">
          {clientId
            ? 'Movimientos de comisión de este cliente, directo del libro de comisiones.'
            : convenioId
            ? 'Movimientos de comisión de este convenio, directo del libro de comisiones.'
            : 'Todos los movimientos de comisión de la organización, directo del libro de comisiones.'}
        </Text>
      </div>

      {/* One vocabulary for the whole page; clicking a tile filters the table. */}
      <div className={`grid grid-cols-2 ${sinPctFianzas.size > 0 ? 'md:grid-cols-5' : 'md:grid-cols-4'} gap-3`}>
        <StatTile
          label="Cobrable ahora"
          value={fmtMXN(saldos.cobrable)}
          tone="green"
          sub="Factura pagada, comisión sin cobrar"
          active={filter === 'cobrable'}
          onClick={() => toggleFilter('cobrable')}
        />
        <StatTile
          label="Aún no exigible"
          value={fmtMXN(saldos.porExigir)}
          tone="amber"
          sub="Factura del cliente sin pagar"
          active={filter === 'porExigir'}
          onClick={() => toggleFilter('porExigir')}
        />
        <StatTile
          label="Cobrada"
          value={fmtMXN(saldos.cobrada)}
          sub="Ya liquidada por la afianzadora"
          active={filter === 'cobrada'}
          onClick={() => toggleFilter('cobrada')}
        />
        <StatTile
          label="Adeudo"
          value={fmtMXN(saldos.adeudo)}
          tone="red"
          sub="Comisión a devolver"
          active={filter === 'adeudo'}
          onClick={() => toggleFilter('adeudo')}
        />
        {sinPctFianzas.size > 0 && (
          <StatTile
            label="Sin %"
            value={String(sinPctFianzas.size)}
            tone="amber"
            sub="Fianzas sin % de comisión"
            active={filter === 'sinPct'}
            onClick={() => toggleFilter('sinPct')}
          />
        )}
      </div>

      {/* The broker's saldo with each afianzadora, from the comisión ledger */}
      {saldos.porAfianzadora.length > 0 && (
        <div className="bg-white border border-azul-surelia/15 rounded-xl shadow-sm overflow-hidden">
          <div className="p-4 border-b border-zinc-200">
            <Heading level={3} className="text-lg text-azul-surelia">
              Saldos con afianzadoras
            </Heading>
            <Text className="text-xs text-azul-surelia/60 mt-0.5">
              Comisión que cada afianzadora le debe al agente, según el libro.
            </Text>
          </div>
          <div className="overflow-x-auto">
            <Table>
              <TableHead className="bg-azul-surelia text-white">
                <TableRow>
                  <TableHeader className="text-white">Afianzadora</TableHeader>
                  <TableHeader className="text-white text-right">
                    <span className="inline-flex items-center gap-1">
                      Cobrable ahora
                      <InfoTip text="Comisión de fianzas con factura pagada que aún no se marca como cobrada. La afianzadora la debe al agente desde ya." />
                    </span>
                  </TableHeader>
                  <TableHeader className="text-white text-right">
                    <span className="inline-flex items-center gap-1">
                      Aún no exigible
                      <InfoTip text="Comisión de fianzas emitidas cuya factura el cliente todavía no paga. Existe en el libro, pero no se puede exigir hasta que la prima se pague." />
                    </span>
                  </TableHeader>
                  <TableHeader className="text-white text-right">
                    <span className="inline-flex items-center gap-1">
                      Adeudo
                      <InfoTip text="Comisión ya cobrada que debe devolverse por fianzas deshechas. Se neta contra futuras liquidaciones." />
                    </span>
                  </TableHeader>
                  <TableHeader className="text-white text-right">
                    <span className="inline-flex items-center gap-1">
                      Neto a favor
                      <InfoTip text="Lo que falta por liquidar con la afianzadora: cobrable + aún no exigible, menos adeudos. Lo ya cobrado no aparece aquí." />
                    </span>
                  </TableHeader>
                </TableRow>
              </TableHead>
              <TableBody>
                {saldos.porAfianzadora.map((a) => (
                  <TableRow key={a.afianzadora}>
                    <TableCell className="font-medium text-zinc-900" title={a.afianzadora}>
                      {afianzadoraLabel(a.afianzadora)}
                    </TableCell>
                    <TableCell
                      className="text-right tabular-nums"
                      title="Fianzas con factura pagada cuya comisión aún no se marca cobrada — exigible ya. Clic para ver las fianzas."
                    >
                      {a.cobrable ? (
                        <button
                          type="button"
                          onClick={() => toggleSaldoDetalle(a.afianzadora, 'cobrable')}
                          className="font-medium text-emerald-700 hover:underline"
                        >
                          {fmtMXN(a.cobrable)}
                        </button>
                      ) : '—'}
                    </TableCell>
                    <TableCell
                      className="text-right tabular-nums"
                      title="Fianzas emitidas cuya factura el cliente aún no paga — la comisión existe pero todavía no es exigible. Clic para ver las fianzas."
                    >
                      {a.porExigir ? (
                        <button
                          type="button"
                          onClick={() => toggleSaldoDetalle(a.afianzadora, 'porExigir')}
                          className="font-medium text-amber-700 hover:underline"
                        >
                          {fmtMXN(a.porExigir)}
                        </button>
                      ) : '—'}
                    </TableCell>
                    <TableCell
                      className="text-right tabular-nums"
                      title="Comisión a devolver por fianzas deshechas; se neta contra futuras liquidaciones. Clic para ver las fianzas."
                    >
                      {a.adeudo ? (
                        <button
                          type="button"
                          onClick={() => toggleSaldoDetalle(a.afianzadora, 'adeudo')}
                          className="font-medium text-red-600 hover:underline"
                        >
                          {fmtMXN(a.adeudo)}
                        </button>
                      ) : '—'}
                    </TableCell>
                    <TableCell className={`text-right tabular-nums font-semibold ${a.neto < 0 ? 'text-red-700' : 'text-azul-surelia'}`}>
                      {fmtMXN(a.neto)}
                    </TableCell>
                  </TableRow>
                ))}
                <TableRow className="bg-azul-surelia/5 border-t-2 border-azul-surelia/20">
                  <TableCell className="font-bold text-azul-surelia">Total</TableCell>
                  <TableCell className="text-right font-bold text-emerald-700 tabular-nums">{fmtMXN(saldos.cobrable)}</TableCell>
                  <TableCell className="text-right font-bold text-amber-700 tabular-nums">{fmtMXN(saldos.porExigir)}</TableCell>
                  <TableCell className="text-right font-bold text-red-600 tabular-nums">{fmtMXN(saldos.adeudo)}</TableCell>
                  <TableCell className={`text-right font-bold tabular-nums ${saldos.neto < 0 ? 'text-red-700' : 'text-azul-surelia'}`}>{fmtMXN(saldos.neto)}</TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </div>
        </div>
      )}

      <div className="bg-white border border-azul-surelia/15 rounded-xl shadow-sm overflow-hidden">
        <div className="p-4 border-b border-zinc-200 flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <Heading level={3} className="text-lg text-azul-surelia">Movimientos</Heading>
            <Badge color="zinc">{visible.length}</Badge>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <div className="relative">
              <MagnifyingGlassIcon className="w-3.5 h-3.5 text-azul-surelia/50 absolute left-2 top-1/2 -translate-y-1/2 pointer-events-none" />
              <input
                type="text"
                value={busqueda}
                onChange={(e) => setBusqueda(e.target.value)}
                placeholder="Buscar movimientos…"
                aria-label="Buscar movimientos"
                className="text-xs border border-azul-surelia/20 rounded-lg pl-7 pr-6 py-1.5 text-azul-surelia w-48 placeholder:text-azul-surelia/40"
              />
              {busqueda && (
                <button
                  type="button"
                  onClick={() => setBusqueda('')}
                  aria-label="Limpiar búsqueda"
                  className="absolute right-1.5 top-1/2 -translate-y-1/2 text-azul-surelia/50 hover:text-azul-surelia"
                >
                  <XMarkIcon className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
            {!clientId && clienteOptions.length > 0 && (
              <SearchableListbox
                className="w-52"
                aria-label="Filtrar por cliente"
                placeholder="Todos los clientes"
                searchPlaceholder="Buscar cliente…"
                value={clienteFilter}
                onChange={setClienteFilter}
                options={[
                  { value: 'todos', label: 'Todos los clientes' },
                  ...clienteOptions.map((c) => ({ value: c.id, label: c.nombre })),
                ]}
              />
            )}
            {afianzadoraOptions.length > 1 && (
              <select
                value={afianzadoraFilter}
                onChange={(e) => setAfianzadoraFilter(e.target.value)}
                className="text-xs border border-azul-surelia/20 rounded-lg px-2 py-1.5 text-azul-surelia max-w-[180px]"
              >
                <option value="todas">Todas las afianzadoras</option>
                {afianzadoraOptions.map((a) => (
                  <option key={a} value={a}>{afianzadoraLabel(a)}</option>
                ))}
              </select>
            )}
            {filter !== 'todas' && (
              <button
                type="button"
                onClick={() => setFilter('todas')}
                className="text-xs text-azul-surelia hover:underline inline-flex items-center gap-1"
              >
                <XMarkIcon className="w-3.5 h-3.5" />
                Quitar filtro de estado
              </button>
            )}
          </div>
        </div>
        <div className="overflow-x-auto">
          <Table>
            <TableHead className="bg-azul-surelia text-white">
              <TableRow>
                <TableHeader className="text-white">Fianza</TableHeader>
                <TableHeader className="text-white">Afianzadora</TableHeader>
                <TableHeader className="text-white">Movimiento</TableHeader>
                <TableHeader className="text-white text-right">Prima</TableHeader>
                <TableHeader className="text-white text-right">Tarifa</TableHeader>
                <TableHeader className="text-white text-right">%</TableHeader>
                <TableHeader className="text-white text-right">Comisión</TableHeader>
                <TableHeader className="text-white">Estado</TableHeader>
              </TableRow>
            </TableHead>
            <TableBody>
              {visible.length === 0 ? (
                <TableRow>
                  <TableCell colSpan="8" className="py-8 text-center text-azul-surelia/70">
                    {clientMovs.length === 0
                      ? 'No hay movimientos en el libro de comisiones.'
                      : busqueda.trim()
                      ? 'Sin movimientos que coincidan con la búsqueda.'
                      : 'Sin movimientos con este filtro.'}
                  </TableCell>
                </TableRow>
              ) : (
                visible.map((m) => (
                  <TableRow key={m.id}>
                    <TableCell>
                      <button
                        type="button"
                        onClick={() => openFianza(m)}
                        className="font-medium text-azul-surelia hover:underline"
                      >
                        {numeroFianzaLabel(m)}
                      </button>
                      {!clientId && (
                        <div
                          className="text-[11px] text-zinc-500 truncate max-w-[200px]"
                          title={clientsById[m.clienteId]?.nombreEmpresa || m.clienteNombre || ''}
                        >
                          {clientsById[m.clienteId]?.nombreEmpresa || m.clienteNombre || ''}
                        </div>
                      )}
                      {m.beneficiarioNombre && (
                        <div
                          className="text-[11px] text-zinc-400 truncate max-w-[200px]"
                          title={`Beneficiario: ${m.beneficiarioNombre}`}
                        >
                          {m.beneficiarioNombre}
                        </div>
                      )}
                    </TableCell>
                    <TableCell className="text-sm" title={m.afianzadora}>
                      {afianzadoraLabel(m.afianzadora)}
                    </TableCell>
                    <TableCell>
                      <div className="text-sm text-zinc-900">{TIPO_LABELS[m.tipo] || m.tipo}</div>
                      <div className="text-[11px] text-zinc-500">{fmtFecha(m.fecha)}</div>
                      {m.splitRol != null && (
                        <div
                          className="mt-0.5 inline-block text-[10px] px-1.5 py-0.5 rounded-full border border-azul-surelia/30 text-azul-surelia bg-azul-surelia/5"
                          title={m.splitRol === 'convenio'
                            ? `Fianza de convenio: recibes el ${m.splitPct}% de la comisión del agente del fiado.`
                            : `Beneficiario con convenio externo: conservas el ${m.splitPct}% de tu comisión.`}
                        >
                          {m.splitRol === 'convenio' ? `Convenio ${m.splitPct}%` : `Conv. externo ${m.splitPct}%`}
                        </div>
                      )}
                    </TableCell>
                    <TableCell className="text-sm text-right tabular-nums">{fmtMXN(m.prima)}</TableCell>
                    <TableCell className="text-sm text-right tabular-nums">
                      <div>{m.tarifa != null ? `${Number(m.tarifa).toFixed(2)}%` : '—'}</div>
                      {m.primaMinima && (
                        <div
                          className="text-[10px] text-amber-600 font-medium"
                          title="Tarifa por encima del tramo más alto: aplica prima mínima."
                        >
                          Prima mín.
                        </div>
                      )}
                    </TableCell>
                    <TableCell className="text-sm text-right tabular-nums">
                      <span className="inline-flex items-center gap-1">
                        <span
                          title={m.comisionPct == null
                            ? diagnoseComisionPct(m, tarifas)?.mensaje
                            : undefined}
                          className={m.comisionPct == null && diagnoseComisionPct(m, tarifas)
                            ? 'text-amber-600 cursor-help'
                            : undefined}
                        >
                          {m.comisionPct != null ? `${m.comisionPct}%` : '—'}
                        </span>
                        {m.pctEstimado && (
                          <span
                            className="text-[10px] px-1 py-0.5 rounded bg-amber-100 text-amber-700 border border-amber-200"
                            title="Comisión estimada con la tabla de comisiones por defecto; confírmala al ver el % real."
                          >
                            por confirmar
                          </span>
                        )}
                        {m.manualOverride && (
                          <span
                            className="text-[10px] px-1 py-0.5 rounded bg-emerald-100 text-emerald-700 border border-emerald-200"
                            title={`% definido manualmente${m.override?.anteriorPct != null ? ` (antes ${m.override.anteriorPct}%)` : ''}.`}
                          >
                            confirmado
                          </span>
                        )}
                        {isOwner && m.tipo === 'emision' && (
                          <button
                            type="button"
                            onClick={() => setEditMov(m)}
                            title="Editar % de comisión de esta emisión"
                            className="text-gray-300 hover:text-azul-surelia"
                          >
                            <PencilSquareIcon className="w-3.5 h-3.5" />
                          </button>
                        )}
                      </span>
                    </TableCell>
                    <TableCell
                      className={`text-sm text-right tabular-nums font-medium ${
                        (Number(m.comisionMonto) || 0) < 0 ? 'text-red-700' : 'text-azul-surelia'
                      }`}
                    >
                      {m.splitRol != null && m.comisionBase != null &&
                        Number(m.comisionBase) !== Number(m.comisionMonto) && (
                        <div
                          className="text-[11px] font-normal text-zinc-400"
                          title="Comisión bruta antes de la participación del convenio"
                        >
                          {fmtMXN(m.comisionBase)} bruta
                        </div>
                      )}
                      {fmtMXN(m.comisionMonto)}
                    </TableCell>
                    <TableCell><EstadoBadge mov={m} /></TableCell>
                  </TableRow>
                ))
              )}
              {visible.length > 0 && (
                <TableRow className="bg-azul-surelia/5 border-t-2 border-azul-surelia/20">
                  <TableCell colSpan="3" className="font-bold text-azul-surelia">
                    Total ({visible.length} movimiento{visible.length === 1 ? '' : 's'})
                  </TableCell>
                  <TableCell className="text-right font-bold text-azul-surelia tabular-nums">
                    {fmtMXN(visible.reduce((s, m) => s + (Number(m.prima) || 0), 0))}
                  </TableCell>
                  <TableCell />
                  <TableCell />
                  <TableCell className="text-right font-bold text-azul-surelia tabular-nums">
                    {fmtMXN(visible.reduce((s, m) => s + (Number(m.comisionMonto) || 0), 0))}
                  </TableCell>
                  <TableCell />
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      </div>

      {/* Slide-over: the fianzas behind one afianzadora's saldo bucket */}
      {saldoDetalle && (() => {
        const row = saldos.porAfianzadora.find((a) => a.afianzadora === saldoDetalle.afianzadora);
        const bucket = saldoDetalle.bucket;
        const bucketMovs = row?.[`${bucket}Movs`] || [];
        if (!row || bucketMovs.length === 0) return null;
        const subtitulo = {
          cobrable: 'Comisión cobrable ahora — facturas pagadas',
          porExigir: 'Comisión aún no exigible — facturas sin pagar',
          adeudo: 'Comisión a devolver',
        }[bucket];
        const totalBucket = row[bucket];
        const nowMs = Date.now();
        const movs = [...bucketMovs].sort(
          (a, b) => (Number(a.fecha) || 0) - (Number(b.fecha) || 0)  // oldest first
        );
        return createPortal(
          <div className="fixed inset-y-0 left-0 z-[60] w-[26rem] max-w-[90vw] flex flex-col bg-white shadow-xl border-r border-azul-surelia/20">
            <div className="bg-azul-surelia px-4 py-3">
              <div className="flex items-center justify-between">
                <div className="min-w-0 flex-1">
                  <h2 className="text-sm font-semibold text-white truncate" title={row.afianzadora}>
                    {afianzadoraLabel(row.afianzadora)}
                  </h2>
                  <p className="text-xs text-white/80">{subtitulo}</p>
                </div>
                <button
                  onClick={() => setSaldoDetalle(null)}
                  className="rounded-lg p-1.5 text-white/80 hover:bg-white/10 hover:text-white transition-colors ml-2"
                >
                  <XMarkIcon className="h-5 w-5" />
                </button>
              </div>
            </div>
            <div className="flex-1 overflow-y-auto p-3">
              <div className="space-y-2">
                {movs.map((m) => {
                  const dias = bucket === 'porExigir' && m.fecha
                    ? Math.floor((nowMs - Number(m.fecha)) / 86400000)
                    : null;
                  return (
                    <div
                      key={m.id}
                      onClick={() => openFianza(m)}
                      className="bg-zinc-50 border border-zinc-200 rounded-lg p-3 hover:border-azul-surelia/40 hover:bg-white cursor-pointer transition-all"
                    >
                      <div className="flex items-start justify-between gap-2 mb-1.5">
                        <div className="flex-1 min-w-0">
                          <div className="text-xs font-semibold text-azul-surelia truncate">
                            {numeroFianzaLabel(m)}
                            {m.tipo && m.tipo !== 'emision' && (
                              <span className="ml-1.5 font-normal text-zinc-500">
                                {TIPO_LABELS[m.tipo] || m.tipo}
                              </span>
                            )}
                          </div>
                          <div className="text-xs text-zinc-600 truncate" title={m.fiadoNombre || ''}>
                            {m.fiadoNombre || clientsById[m.clienteId]?.nombreEmpresa || m.clienteNombre || 'Sin fiado'}
                          </div>
                          {m.convenioNombre && (
                            <div className="text-[11px] text-zinc-400 truncate" title={m.beneficiarioNombre || ''}>
                              {m.convenioNombre}
                              {m.beneficiarioNombre ? ` · ${m.beneficiarioNombre}` : ''}
                            </div>
                          )}
                        </div>
                        {dias !== null && (
                          <span
                            className={`shrink-0 text-[10px] font-semibold px-1.5 py-0.5 rounded ${
                              dias >= 90
                                ? 'bg-red-100 text-red-800'
                                : dias >= 60
                                  ? 'bg-amber-100 text-amber-800'
                                  : 'bg-zinc-100 text-zinc-600'
                            }`}
                            title={dias >= 90
                              ? 'Más de 90 días sin pago: la afianzadora puede reportar al cliente en buró de crédito.'
                              : 'Días desde la emisión sin que el cliente pague la factura.'}
                          >
                            {dias} días
                          </span>
                        )}
                      </div>
                      <div className="flex flex-wrap items-center gap-1 mb-1.5">
                        <span
                          className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-full ${
                            facturaPagada(m) ? 'bg-green-100 text-green-800' : 'bg-orange-100 text-orange-700'
                          }`}
                        >
                          {facturaPagada(m) ? 'Factura pagada' : 'Factura sin pagar'}
                        </span>
                        {m.splitRol != null && (
                          <span
                            className="text-[10px] px-1.5 py-0.5 rounded-full border border-azul-surelia/30 text-azul-surelia bg-azul-surelia/5"
                            title={m.splitRol === 'convenio'
                              ? `Fianza de convenio: recibes el ${m.splitPct}% de la comisión del agente del fiado.`
                              : `Beneficiario con convenio externo: conservas el ${m.splitPct}% de tu comisión.`}
                          >
                            {m.splitRol === 'convenio' ? `Convenio ${m.splitPct}%` : `Conv. externo ${m.splitPct}%`}
                          </span>
                        )}
                        {m.pctEstimado && (
                          <span
                            className="text-[10px] px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700 border border-amber-200"
                            title="Comisión estimada con la tabla de comisiones por defecto; confírmala al ver el % real."
                          >
                            % por confirmar
                          </span>
                        )}
                      </div>
                      <div className="grid grid-cols-3 gap-1 text-xs">
                        <div>
                          <div className="text-zinc-500">Prima</div>
                          <div className="font-semibold text-zinc-900 tabular-nums">{fmtMXN(m.prima)}</div>
                        </div>
                        <div>
                          <div className="text-zinc-500">%</div>
                          <div className="font-semibold text-zinc-900 tabular-nums">
                            {m.comisionPct != null ? `${m.comisionPct}%` : '—'}
                          </div>
                        </div>
                        <div>
                          <div className="text-zinc-500">Comisión</div>
                          <div className="font-semibold text-azul-surelia tabular-nums">{fmtMXN(m.comisionMonto)}</div>
                          {m.splitRol != null && m.comisionBase != null &&
                            Number(m.comisionBase) !== Number(m.comisionMonto) && (
                            <div className="text-[10px] font-normal text-zinc-400 tabular-nums" title="Comisión bruta antes de la participación del convenio">
                              {fmtMXN(m.comisionBase)} bruta
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="mt-1.5 flex items-center justify-between text-[11px] text-zinc-400">
                        <span className="truncate" title={m.contratoNumero || ''}>
                          {m.contratoNumero || 'Sin contrato'}
                        </span>
                        <span className="shrink-0">{fmtFecha(m.fecha)}</span>
                      </div>
                    </div>
                  );
                })}

                <div className="border-t border-zinc-200 pt-2 mt-3 space-y-1">
                  <div className="flex justify-between text-xs">
                    <span className="text-zinc-600">{movs.length} fianza{movs.length === 1 ? '' : 's'}</span>
                  </div>
                  <div className="flex justify-between text-xs">
                    <span className="text-zinc-600">Total</span>
                    <span className="font-semibold text-azul-surelia">{fmtMXN(totalBucket)}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>,
          document.body
        );
      })()}

      {/* % editor — the same dialog FianzaView's ledger panel uses, with the
          sin-% diagnosis inside. %-only here (split editing needs the
          participación context, which lives in FianzaView). */}
      {editMov && (
        <ComisionEditorDialog
          isOpen
          onClose={() => setEditMov(null)}
          fianza={{ id: editMov.fianzaId, numeroFianza: editMov.numeroFianza }}
          contratoId={editMov.contratoId}
          movimiento={editMov}
          participacion={null}
          showSplit={false}
        />
      )}

      {/* FianzaView full-screen component - rendered via portal with higher z-index */}
      {selectedFianza && createPortal(
        <div className="relative z-[70]">
          <FianzaView
            isOpen={!!selectedFianza}
            onClose={() => setSelectedFianza(null)}
            fianza={selectedFianza}
            clientData={clientsById[clientId || selectedFianza.clienteIdRef] || {}}
            contratoData={selectedFianza.contratoRef || {}}
          />
        </div>,
        document.body
      )}
    </div>
  );
}

export default ClientComisiones;
